import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Navbar from './components/Navbar';
import Home from './pages/Home';
import CityDetails from './pages/CityDetails';
import About from './pages/About';
import Contact from './pages/Contact';
import Login from './pages/Login';
import SignUp from './pages/SignUp';
import FeatureDetails from './components/FeatureDetails';
import CurtainAnimation from './components/CurtainAnimation';
import { supabase } from './config/supabaseClient';
import { Session } from '@supabase/supabase-js';
import './styles/globals.css';
import './styles/LoginLoader.css';

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showLoginCurtain, setShowLoginCurtain] = useState(false);
  const [isCurtainOpen, setIsCurtainOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Check current auth session when app loads
    supabase.auth.getSession().then(({ data: { session } }: { data: { session: Session | null } }) => {
      setIsLoggedIn(!!session);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event: string, session: Session | null) => {
      setIsLoggedIn(!!session);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleLogin = () => {
    // Don't set logged in state immediately
    // First show loader and curtain animation
    setIsLoading(true);
    setShowLoginCurtain(true);
    
    // After loader (2.5s), show curtain
    setTimeout(() => {
      setIsLoading(false);
      setIsCurtainOpen(true);
      
      // After curtain animation (3.5s), set logged in
      setTimeout(() => {
        setIsCurtainOpen(false);
        // Wait for curtain close animation
        setTimeout(() => {
          setShowLoginCurtain(false);
          setIsLoggedIn(true); // Now navigate to home
        }, 1000);
      }, 3500);
    }, 2500);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setIsLoggedIn(false);
  };

  return (
    <Router>
      <div className="app">
        {/* Login Animations */}
        {showLoginCurtain && (
          <>
            <CurtainAnimation isOpen={isCurtainOpen} />
            <div className="login-loader" style={{
              display: isLoading ? 'flex' : 'none',
              opacity: isLoading ? 1 : 0
            }}>
              <div className="loader-content">
                <div className="loader-spinner"></div>
                <p>Welcome to TripGo...</p>
              </div>
            </div>
          </>
        )}
        <Navbar isLoggedIn={isLoggedIn} onLogout={handleLogout} />
        <main>
          <Routes>
            <Route
              path="/"
              element={
                isLoggedIn ? (
                  <Home />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/city/:cityId"
              element={
                isLoggedIn ? (
                  <CityDetails />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/about"
              element={
                isLoggedIn ? (
                  <About />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/contact"
              element={
                isLoggedIn ? (
                  <Contact />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/login"
              element={
                isLoggedIn ? (
                  <Navigate to="/" replace />
                ) : (
                  <Login onLogin={handleLogin} />
                )
              }
            />
            <Route
              path="/signup"
              element={
                isLoggedIn ? (
                  <Navigate to="/" replace />
                ) : (
                  <SignUp />
                )
              }
            />
            <Route
              path="/features/:featureId"
              element={
                isLoggedIn ? (
                  <FeatureDetails />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
          </Routes>
        </main>

      </div>
    </Router>
  );
}

export default App;